import {
  codeSentResponseSchema,
  offerPasskeyResponseSchema,
  passkeyOptionsResponseSchema,
  socialStartResponseSchema,
  errorResponseSchema,
  finishedResponseSchema,
  interactionStateSchema,
  offerPasswordResponseSchema,
  profileRequiredResponseSchema,
  requireBirthdateResponseSchema,
  requireTermsResponseSchema,
  type CompleteProfileRequest,
  type FinishedResponse,
  type InteractionState,
  type OfferPasskeyResponse,
  type OfferPasswordResponse,
  type ProfileRequiredResponse,
  type RequireBirthdateResponse,
  type RequireTermsResponse,
} from '@repo/auth-contracts';
import type { ZodType } from 'zod';

/** A failed interaction call, reduced to a stable machine code. */
export class InteractionApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    // The message is for logs only — the UI renders `messageFor(code)`.
    super(`interaction api error: ${code} (${status})`);
    this.name = 'InteractionApiError';
  }
}

/**
 * Any login-completing call may answer with a post-login step instead of the
 * redirect: the two optional offers, or the mandatory age gate (ADR 0021).
 */
export type Completion =
  | FinishedResponse
  | OfferPasswordResponse
  | OfferPasskeyResponse
  | RequireBirthdateResponse
  | RequireTermsResponse;

export interface InteractionApi {
  getState(uid: string): Promise<InteractionState>;
  /**
   * Tells the relying party "no": denies the interaction so it receives
   * access_denied instead of waiting on a window that was abandoned. The one
   * caller today is the under-age refusal, where the form can never succeed.
   */
  abort(uid: string, csrfToken: string): Promise<FinishedResponse>;
  startEmailLogin(
    uid: string,
    email: string,
    csrfToken: string,
    locale?: 'en' | 'de',
  ): Promise<void>;
  /** Resolves to the completed login, or to "we still need a profile". */
  verifyEmailCode(
    uid: string,
    code: string,
    csrfToken: string,
    /** Explicit selection only — persisted to the account on success. */
    locale?: 'en' | 'de',
  ): Promise<ProfileRequiredResponse | Completion>;
  /** The fallback branch; succeeding here proves a password already exists. */
  passwordLogin(
    uid: string,
    email: string,
    password: string,
    csrfToken: string,
    /** Explicit selection only — persisted to the account on success. */
    locale?: 'en' | 'de',
  ): Promise<Completion>;
  /**
   * Answers the mandatory age gate (ADR 0021). Succeeds into whatever the
   * login owes next, which may itself be one of the optional offers.
   */
  supplyBirthdate(
    uid: string,
    birthdate: string,
    csrfToken: string,
  ): Promise<Completion>;
  setPassword(
    uid: string,
    password: string,
    csrfToken: string,
  ): Promise<FinishedResponse>;
  skipPassword(uid: string, csrfToken: string): Promise<FinishedResponse>;
  /** Begins a federation handshake; the caller navigates to the returned URL. */
  startSocial(
    uid: string,
    provider: string,
    csrfToken: string,
  ): Promise<string>;
  /** Completes the login the social callback held for this interaction. */
  completeSocial(uid: string, csrfToken: string): Promise<Completion>;
  /** WebAuthn ceremony options; the browser turns these into a credential. */
  passkeyOptions(
    uid: string,
    path: 'passkey/start' | 'passkey/enrol/start',
    csrfToken: string,
  ): Promise<Record<string, unknown>>;
  passkeyFinish(
    uid: string,
    path: 'passkey/finish' | 'passkey/enrol/finish' | 'passkey/enrol/skip',
    response: Record<string, unknown> | null,
    csrfToken: string,
  ): Promise<Completion>;
  decideConsent(
    uid: string,
    decision: 'allow' | 'deny',
    csrfToken: string,
  ): Promise<FinishedResponse>;
  completeProfile(
    uid: string,
    body: CompleteProfileRequest,
    csrfToken: string,
  ): Promise<FinishedResponse>;

  /**
   * Accepts the legal documents in force. Carries agreement only — the
   * server decides which versions that was
   * (docs/auth/40-features/terms-acceptance.md).
   */
  acceptTerms(uid: string, csrfToken: string): Promise<Completion>;
}

const completionSchema = finishedResponseSchema
  .or(offerPasswordResponseSchema)
  .or(offerPasskeyResponseSchema)
  .or(requireBirthdateResponseSchema)
  .or(requireTermsResponseSchema);

async function parseOrThrow<T>(res: Response, schema: ZodType<T>): Promise<T> {
  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    // 429 has no body contract of its own; map it to a dedicated code so the
    // copy map can say something useful.
    if (res.status === 429) throw new InteractionApiError('rate_limited', 429);
    const parsed = errorResponseSchema.safeParse(body);
    throw new InteractionApiError(
      parsed.success ? parsed.data.error : 'unknown',
      res.status,
    );
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) throw new InteractionApiError('unknown', res.status);
  return parsed.data;
}

/**
 * Client for the interaction API (ADR 0010). Cross-origin, so every call sends
 * credentials to carry the SameSite=Lax interaction cookie, and every mutation
 * echoes the double-submit CSRF token.
 */
export function createInteractionApi(baseUrl: string): InteractionApi {
  const base = `${baseUrl.replace(/\/+$/, '')}/interaction/api`;

  async function send<T>(
    path: string,
    schema: ZodType<T>,
    init?: { body?: unknown; csrfToken?: string },
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method: init?.body === undefined ? 'GET' : 'POST',
        credentials: 'include',
        headers: {
          accept: 'application/json',
          ...(init?.body !== undefined && {
            'content-type': 'application/json',
          }),
          ...(init?.csrfToken && { 'x-csrf-token': init.csrfToken }),
        },
        ...(init?.body !== undefined && { body: JSON.stringify(init.body) }),
      });
    } catch {
      throw new InteractionApiError('network', 0);
    }
    return parseOrThrow(res, schema);
  }

  return {
    getState: (uid) =>
      send(`/${encodeURIComponent(uid)}`, interactionStateSchema),

    abort: (uid, csrfToken) =>
      send(`/${encodeURIComponent(uid)}/abort`, finishedResponseSchema, {
        body: {},
        csrfToken,
      }),

    startEmailLogin: async (uid, email, csrfToken, locale) => {
      await send(
        `/${encodeURIComponent(uid)}/login/email/start`,
        codeSentResponseSchema,
        // The locale rides along so a STRANGER's code mail arrives in the
        // language of the screen that asked for it (contract note).
        { body: { email, ...(locale ? { locale } : {}) }, csrfToken },
      );
    },

    verifyEmailCode: (uid, code, csrfToken, locale) =>
      send(
        `/${encodeURIComponent(uid)}/login/email/verify`,
        // Any of these is a valid success shape.
        completionSchema.or(profileRequiredResponseSchema),
        { body: { code, ...(locale ? { locale } : {}) }, csrfToken },
      ),

    decideConsent: (uid, decision, csrfToken) =>
      send(`/${encodeURIComponent(uid)}/consent`, finishedResponseSchema, {
        body: { decision },
        csrfToken,
      }),

    passwordLogin: (uid, email, password, csrfToken, locale) =>
      send(`/${encodeURIComponent(uid)}/login/password`, completionSchema, {
        body: { email, password, ...(locale ? { locale } : {}) },
        csrfToken,
      }),

    supplyBirthdate: (uid, birthdate, csrfToken) =>
      send(`/${encodeURIComponent(uid)}/profile/birthdate`, completionSchema, {
        body: { birthdate },
        csrfToken,
      }),

    acceptTerms: (uid, csrfToken) =>
      send(`/${encodeURIComponent(uid)}/terms/accept`, completionSchema, {
        body: { acceptTerms: true, acceptPrivacy: true },
        csrfToken,
      }),

    setPassword: (uid, password, csrfToken) =>
      send(`/${encodeURIComponent(uid)}/password/set`, finishedResponseSchema, {
        body: { password },
        csrfToken,
      }),

    skipPassword: (uid, csrfToken) =>
      send(
        `/${encodeURIComponent(uid)}/password/skip`,
        finishedResponseSchema,
        {
          body: {},
          csrfToken,
        },
      ),

    startSocial: async (uid, provider, csrfToken) =>
      (
        await send(
          `/${encodeURIComponent(uid)}/social/${encodeURIComponent(provider)}/start`,
          socialStartResponseSchema,
          { body: {}, csrfToken },
        )
      ).authorizationUrl,

    completeSocial: (uid, csrfToken) =>
      send(`/${encodeURIComponent(uid)}/social/complete`, completionSchema, {
        body: {},
        csrfToken,
      }),

    passkeyOptions: async (uid, path, csrfToken) =>
      (
        await send(
          `/${encodeURIComponent(uid)}/${path}`,
          passkeyOptionsResponseSchema,
          { body: {}, csrfToken },
        )
      ).options,

    passkeyFinish: (uid, path, response, csrfToken) =>
      send(`/${encodeURIComponent(uid)}/${path}`, completionSchema, {
        body: response ? { response } : {},
        csrfToken,
      }),

    completeProfile: (uid, body, csrfToken) =>
      send(
        `/${encodeURIComponent(uid)}/signup/complete`,
        finishedResponseSchema,
        { body, csrfToken },
      ),
  };
}
